import type { CodeType, GridCodeType } from "./codeTypes";
import type { ElixhauserFlag } from "./elixhauser";
import type { CodeValidation } from "./encounterServiceEntity";
import type { IdDescriptionBase } from "./patientEncounter";
import type { DDLValues } from ".";

export enum PresentOnAdmission {
  Y = "Y",
  N = "N",
  U = "U",
  W = "W",
  ONE = "1",
  EMPTY = "",
}

const Y = { title: "Yes", id: PresentOnAdmission.Y, ViewId: PresentOnAdmission.Y };
const N = { title: "No", id: PresentOnAdmission.N, ViewId: PresentOnAdmission.N };
const U = { title: "Unknown", id: PresentOnAdmission.U, ViewId: PresentOnAdmission.U };
const W = {
  title: "Clinically undetermined",
  id: PresentOnAdmission.W,
  ViewId: PresentOnAdmission.W,
};
const ONE = {
  title: "Unreported / Not used (Exempt from POA reporting)",
  id: PresentOnAdmission.ONE,
  ViewId: PresentOnAdmission.ONE,
};

export const PresentOnAdmissionMap = {
  1: ONE,
  N,
  U,
  W,
  Y,
};

export const PresentOnAdmissionCollection: DDLValues[] = [Y, N, U, W, ONE];

export const PresentOnAdmissionList: IdDescriptionBase[] = [
  { title: "Yes", id: PresentOnAdmission.Y, ViewId: PresentOnAdmission.Y },
  { title: "No", id: PresentOnAdmission.N, ViewId: PresentOnAdmission.N },
  { title: "Unknown", id: PresentOnAdmission.U, ViewId: PresentOnAdmission.U },
  { title: "Clinically undetermined", id: PresentOnAdmission.W, ViewId: PresentOnAdmission.W },
  {
    title: "Unreported / Not used (Exempt from POA reporting)",
    id: PresentOnAdmission.ONE,
    ViewId: PresentOnAdmission.ONE,
  },
];

export interface Provider {
  id: number;
  name: string;
  service: string;
}

export const ProviderCollection: Provider[] = [
  { id: 1, name: "Provider1", service: "Service1" },
  { id: 2, name: "Provider2", service: "Service4" },
  { id: 3, name: "Provider3", service: "Service3" },
];

export interface BaseCodeRow {
  autoChanged?: string[];
  code?: string;
  type?: CodeType; // TODO: make mandatory after SVC-1617 type resolved
  gridCodeType: GridCodeType;
  presentOnAdmission?: PresentOnAdmission | null;
  description?: string; // currently shown description. Calculate it from custom and server
  id: string;
  grouperFlag?: string;
  grouperFlagAffectsDRG?: boolean; // has AD flag to show some flags with bold and to create tooltip text
  grouperFlagHFlag?: boolean; // has H flag to show some flags with bold
  episode?: string;
  provider?: string;
  date?: Date | string;
  edits?: any[];
  mneEdits?: any[];
  modifiers?: string[];
  units?: string;
  charges?: string;
  nccharges?: string;
  apc?: string;
  hccs?: any[]; // all hccs
  hccs24?: any[]; // versioned hccs
  hccs28?: any[]; // versioned hccs
  // Exempt POA flag: undefined - no exempt / null - blank / '1' - '1'
  exemptPoa?: string | null;
  valid?: boolean;
  validationMessages?: string[];
  empty?: boolean;
  inEdit?: boolean;
  loadingValidity?: boolean;
  revenueCode?: string;
  drg?: string;
  drgDescription?: string;
  drgWeight?: number;
  reimbursement?: number;
  mortalityIndexTotal?: number | null;
  readmissionIndexTotal?: number | null;
  elixhauserCategories?: ElixhauserFlag[];
  editField?: string;
  procedureData?: any;
  initialCode?: string;
  serviceId?: string | null;
  serviceCode?: string | null; // stored on the services side code
  serviceCustomDescription?: string | null; // stored on the services side description
  customCodeDescription?: string | null; // raw user description
  loaded?: boolean; // indicates that code was synchronized with services
  psychComorbidityAdjustmentFactor?: string | null;
  psychComorbidityCategoryDescription?: string | null;
  IsChargeDriven?: boolean; // this code cannot be edited

  // service side code description
  ServiceCodeDescription?: string | null;

  // all code validations
  Validations: CodeValidation[];

  // Dx Links array
  DxLinks?: string[] | null;

  // place of service (professional procedure line) practice mode
  placeOfService?: string;
}

export type KeyedCodeRowCollection = Record<string, BaseCodeRow>;

export interface BaseCodeRowCollection {
  items: KeyedCodeRowCollection;
  last: string;
}

export interface EncounterCodeGridState {
  rows: BaseCodeRowCollection;
  currentRow: string;
}

export interface CodeGroup {
  codes: BaseCodeRow[];
  editedCode?: BaseCodeRow;
  locked?: boolean;
}
